/**
 * Une IFactory est le constructeur d'une class qui permet d'instancier
 * un objet à partir d'un forme JSON ou Struct
 * (https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm,
 * http://w3c.github.io/html/infrastructure.html#safe-passing-of-structured-data).
 */
interface IFactory<T> {
	/**
	 * String unique identifiant le type de T et sa IFactory associée.
	 */
	type: string;

	/** Registry pour trouver les IFactory<T> en fonction d'un type.*/
	registry: FactoryRegistry<T>;

	/**
	 * @param json Doit posséder une propriété "type" correspondant à la factory.
	 */
	buildFromJson(json: IFactoryInput): T;

	/**
	 * @param struct Doit posséder une propriété "type" correspondant à la factory.
	 */
	buildFromStruct(struct: IFactoryInput): T;

}

interface IFactoryInput {
	type: string;
}

/**
 * Registre de IFactory d'objets d'un certain.
 * Une instance de ce FactoryRegistry doit être déclarée en champ statique
 * "registry" de la classe racine T (cf IFactory.registry).
 */
class FactoryRegistry<T> {
	name: string;
	protected _parent: FactoryRegistry<T>;
	protected _msgFactories: Dict<IFactory<T>>;

	constructor(name: string, parent?: FactoryRegistry<T>) {
		this.name = name;
		this._parent = parent;
		this._msgFactories = {};
	}

	register(factory: IFactory<T>) {
		this._msgFactories[factory.type] = factory;
	}

	getFactory(type: string): IFactory<T> {
		let factory = this._msgFactories[type];
		if (!factory) {
			if (this._parent) factory = this._parent.getFactory(type);
			if (!factory) throw Error(`Type '${type}' not found in FactoryRegistry ${this.name}`);
		}
		return factory;
	}

	newFromJson(json: IFactoryInput): T {
		const fact = this.getFactory(json.type);
		return fact ? fact.buildFromJson(json) : null;
	}

	newFromStruct(struct: IFactoryInput): T {
		const fact = this.getFactory(struct.type);
		return fact ? fact.buildFromStruct(struct) : null;
	}
}

/** Voir Serializable.writeTo() */
export enum ESerialMode{
	json, struct, clone
}

/**
 * Classe générique racine pour les objets sérialisables.
 */
abstract class Serializable {

	static buildFromJson(this: ObjectConstructor, json: any): any {
		return (new this() as Serializable).readFrom(json, ESerialMode.json);
	}

	static buildFromStruct(this: ObjectConstructor, struct: any): any {
		return (new this() as Serializable).readFrom(struct, ESerialMode.struct);
	}

	/** Type utilisé dans la sérialisation json / struct. */
	get type(): string {
		return (this.constructor as any).type;
	}

	/**
	 * Clonage profond, ie sans dépendance, alternative plus sécurisée de Object.create(this);
	 */
	clone(): this {
		const clone = new (this.constructor as any)() as this;
		this.writeTo(clone, ESerialMode.clone);
		return clone;
	}

	/** Retourne une forme Json sérialisableg. */
	toJSON(): any {
		const json = Object.create(null);
		json.type = this.type;
		this.writeTo(json, ESerialMode.json);
		return json;
	}

	/**
	 * Retourne une forme "Structured Object" de ce Msg compatible avec worker.postMesssage().
	 * Surchargeable en cas de différence avec Json.
	 */
	toStruct(): any {
		const struct = Object.create(null);
		struct.type = this.type;
		this.writeTo(struct, ESerialMode.struct);
		return struct;
	}

	/**
	 * Recopie des propriétés de l'objet. La propriété "type" relative au type de l'objet a déjà été traitée.
	 * @param o Objet sur lequel recopier les propriétés en fonction du ESerialMode demandé.
	 * @param mode Les stratégies de copie peuvent s'adapter à l'usage cible :
	 *  - ESerialMode.json: Utilisé via JSON.stringify().
	 *      o peut contenir des objets riches possédant une méthode toJSON();
	 *      Inutile de cloner les tableaux et objets de données primitives.
	 *  - ESerialMode.struct: Utilisé pour une sérialisation de type "structured clone algorithm"
	 *      o ne peut pas contenir d'objets riches possédant une méthode toJSON();
	 *      o peut contenir les objets autorisés par https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
	 *      Inutile de cloner les tableaux et objets de données primitives.
	 *  - ESerialMode.clone: Utilisé pour cloner un objet. o est alors l'objet cloné (construit avec le même constructor que this).
	 *      Tous les tableaux et objets mutables de données primitives DOIVENT être clonés.
	 */
	abstract writeTo(o: any, mode: ESerialMode): void;

	/**
	 * Lecture à partir d'un objet issu d'une désérialisation.
	 * @param o Objet désérialisé pour récupérer les propriétés
	 * @param mode Mode de sérialisation utilisée. ESerialMode.clone n'est jamais utilisé ici.
	 */
	abstract readFrom(o: any, mode: ESerialMode): this;


	toString(): string {return JSON.stringify(this.toJSON())}

	/**
	 * Sérialisation d'un objet Serializable.
	 */
	static serialSerializable(o: Serializable, mode: ESerialMode): any {
		if (o == null) return o; //null ou undefined;
		switch (mode) {
		case ESerialMode.json:
			return o;
		case ESerialMode.clone:
			return o.clone();
		case ESerialMode.struct:
			return o.toStruct();
		}
		throw Error("unknown mode");
	}

	/**
	 * Désérialisation d'un objet Serializable.
	 */
	static deserialSerializable<T>(o: Serializable, mode: ESerialMode, reg: FactoryRegistry<T>): T {
		switch (mode) {
		case ESerialMode.json:
			return reg.newFromJson(o);
		case ESerialMode.struct:
			return reg.newFromStruct(o);
		}
		throw Error("unknown mode");
	}

	/**
	 * Sérialisation d'un tableau qui contient des données primitives / immutables (string, number, json...).
	 * Le tableau étant modifiable, il doit être cloné en cas de mode "clone".
	 */
	static serialArrayPrimitives(a: Array<any>, mode: ESerialMode): any {
		return mode === ESerialMode.clone ? a.concat() : a;
	}

	/**
	 * Sérialisation d'un objet qui ne contient que des données primitives / immutables (string, number, json...).
	 * L'objet étant lui-même modifiable, il doit être cloné en cas de mode "clone".
	 */
	static serialObjectPrimitives(o: any, mode: ESerialMode): any {
		return mode === ESerialMode.clone ? Object.assign({}, o) : o;
	}

	/**
	 * Sérialisation d'un tableau d'objets Serializables.
	 */
	static serialArraySerializables(a: Array<Serializable>, mode: ESerialMode): any {
		switch (mode) {
		case ESerialMode.json:
			return a;
		case ESerialMode.clone:
			return a.map(m => m.clone());
		case ESerialMode.struct:
			return a.map(m => m.toStruct());
		}
		throw Error("unknown mode");
	}

	/**
	 * Désérialisation d'un tableau d'objets Serializables.
	 */
	static deserialArraySerializables<T>(a: Array<Serializable>, mode: ESerialMode, reg: FactoryRegistry<T>): Array<T> {
		switch (mode) {
		case ESerialMode.json:
			return a.map(m => reg.newFromJson(m));
		case ESerialMode.struct:
			return a.map(m => reg.newFromStruct(m));
		}
		throw Error("unknown mode");
	}

	/**
	 * Sérialisation d'un objet simple ne contenant que des objets Serializables.
	 */
	static serialObjectSerializables(o: any, mode: ESerialMode): any {
		switch (mode) {
		case ESerialMode.json:
			return o;
		case ESerialMode.clone: {
			const res: Dict<any> = {};
			for (const prop in o) {
				res[prop] = o[prop].clone();
			}
			return res;
		}
		case ESerialMode.struct: {
			const res = Object.create(null);
			for (const prop in o) {
				res[prop] = o[prop].toStruct();
			}
			return res;
		}
		}
		throw Error("unknown mode");
	}

	// /**
	//  * Méthode générique "défensive" de sérialisation.
	//  *
	//  * Si la propriété est une fonction, undefined est retourné.
	//  *
	//  * Si une propriété de l'objet n'est pas connue et la
	//  * sérialisation n'est pas sûre, une exception est levée.
	//  */
	// static serialProp(o: any, mode: ESerialMode): any {
	//     switch(typeof o) {
	//         case 'objet':
	//             if(Array.isArray(o)) return o.map( m => Serializable.serialProp(m, mode));
	//             if(o instanceof Serializable) return Serializable.serialSerializable(o, mode);
	//             //autre types connus...
	//             break;
	//         case 'string' :
	//         case 'boolean' :
	//         case 'number' : return o;
	//         case 'undefined' :
	//         case 'function': return undefined;
	//         case 'symbol': if(mode===ESerialMode.clone) return o;
	//     }
	//     throw Error(`Property not serializable: ${o}`);
	// }
	//
	//
	// static serialPropsTo(from: any, props:Array<string>, mode: ESerialMode, to:any) {
	//     props.forEach( p => {
	//             let v = Serializable.serialProp(from[p], mode);
	//             if(v !== undefined) to[p] = v;
	//         }
	//     );
	// }

	/**
	 * Sérialisation d'une Date (pas super utile, c'est un exemple pour
	 * les objets gérés par le "structured clone algorithm").
	 */
	static serialDate(d: Date, mode: ESerialMode): any {
		return mode === ESerialMode.struct ? d : d.getTime();
	}

	/**
	 * Désérialisation d'une Date (pas super utile, c'est un exemple pour
	 * les objets gérés par le "structured clone algorithm").
	 */
	static deserialDate(d: any, mode: ESerialMode): Date {
		return mode === ESerialMode.struct ? d : new Date(d);
	}
}


export{IFactory, FactoryRegistry, Serializable}
